import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Divider,
  Drawer,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import PageContainer from "../../../components/PageContainer";
import AppDataTable from "../../../components/AppDataTable";
import {
  actualizarServicio,
  crearServicio,
  desactivarServicio,
  getServicios,
} from "../../../services/modules/ServiciosCatalogo";

const formatMoney = (value) =>
  new Intl.NumberFormat("es-GT", { style: "currency", currency: "GTQ" }).format(Number(value || 0));

const ServiciosCatalogo = () => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [formData, setFormData] = useState({
    id_servicio: null,
    codigo: "",
    nombre: "",
    categoria: "",
    precio_base: 0,
    costo_externo_base: 0,
    porcentaje_recargo_costo_ext: 0,
    activo: true,
  });

  const fetchAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getServicios({ activos: "1" });
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.message || "Error al cargar servicios.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const openPanel = (row = null) => {
    if (row) {
      setFormData({
        id_servicio: row.id_servicio,
        codigo: row.codigo || "",
        nombre: row.nombre || "",
        categoria: row.categoria || "",
        precio_base: Number(row.precio_base || 0),
        costo_externo_base: Number(row.costo_externo_base || 0),
        porcentaje_recargo_costo_ext: Number(row.porcentaje_recargo_costo_ext || 0),
        activo: Boolean(row.activo),
      });
    } else {
      setFormData({
        id_servicio: null,
        codigo: "",
        nombre: "",
        categoria: "",
        precio_base: 0,
        costo_externo_base: 0,
        porcentaje_recargo_costo_ext: 0,
        activo: true,
      });
    }
    setDrawerOpen(true);
  };

  const closePanel = () => {
    if (processing) return;
    setDrawerOpen(false);
  };

  const handleSave = async () => {
    setProcessing(true);
    setError(null);
    try {
      const payload = {
        codigo: formData.codigo || null,
        nombre: formData.nombre,
        categoria: formData.categoria || null,
        precio_base: Number(formData.precio_base || 0),
        costo_externo_base: Number(formData.costo_externo_base || 0),
        porcentaje_recargo_costo_ext: Number(formData.porcentaje_recargo_costo_ext || 0),
        activo: formData.activo ? 1 : 0,
      };

      if (formData.id_servicio) {
        await actualizarServicio(formData.id_servicio, payload);
      } else {
        await crearServicio(payload);
      }
      setDrawerOpen(false);
      await fetchAll();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Error al guardar servicio.");
    } finally {
      setProcessing(false);
    }
  };

  const handleDisable = async (row) => {
    if (!row?.id_servicio) return;
    if (!window.confirm(`¿Desactivar servicio "${row.nombre}"?`)) return;
    setProcessing(true);
    setError(null);
    try {
      await desactivarServicio(row.id_servicio);
      await fetchAll();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Error al desactivar servicio.");
    } finally {
      setProcessing(false);
    }
  };

  const columns = useMemo(
    () => [
      { field: "codigo", headerName: "Código", flex: 0.8, minWidth: 120, sortable: true, valueGetter: (_v, r) => r?.codigo || "" },
      { field: "nombre", headerName: "Servicio", flex: 1.5, minWidth: 220, sortable: true },
      { field: "categoria", headerName: "Categoría", flex: 1, minWidth: 160, sortable: true, valueGetter: (_v, r) => r?.categoria || "" },
      {
        field: "precio_base",
        headerName: "Precio",
        flex: 0.8,
        minWidth: 140,
        sortable: true,
        align: "right",
        headerAlign: "right",
        valueGetter: (_v, r) => Number(r?.precio_base || 0),
        renderCell: ({ row }) => formatMoney(row.precio_base),
      },
      {
        field: "costo_externo_base",
        headerName: "Costo ext.",
        flex: 0.8,
        minWidth: 150,
        sortable: true,
        align: "right",
        headerAlign: "right",
        valueGetter: (_v, r) => Number(r?.costo_externo_base || 0),
        renderCell: ({ row }) => formatMoney(row.costo_externo_base),
      },
      {
        field: "porcentaje_recargo_costo_ext",
        headerName: "Recargo %",
        flex: 0.7,
        minWidth: 140,
        sortable: true,
        align: "right",
        headerAlign: "right",
        valueGetter: (_v, r) => Number(r?.porcentaje_recargo_costo_ext || 0),
      },
      {
        field: "acciones",
        headerName: "Acciones",
        minWidth: 220,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="outlined" onClick={() => openPanel(row)}>
              Editar
            </Button>
            <Button size="small" color="error" onClick={() => handleDisable(row)}>
              Desactivar
            </Button>
          </Stack>
        ),
      },
    ],
    []
  );

  return (
    <PageContainer title="Catálogo de servicios">
      <Stack spacing={2}>
        {error && <Alert severity="error">{error}</Alert>}
        <Stack
          direction={isXs ? "column" : "row"}
          spacing={2}
          justifyContent="space-between"
          alignItems={isXs ? "stretch" : "center"}
        >
          <Typography variant="body2" color="text.secondary">
            Define servicios con su precio base y costo externo (boletas, recargas, etc).
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => openPanel(null)}>
            Nuevo servicio
          </Button>
        </Stack>

        <AppDataTable rows={items} columns={columns} loading={loading} />
      </Stack>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={closePanel}
        PaperProps={{ sx: { width: { xs: "100%", sm: 460 }, p: 2 } }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight={700}>
            {formData.id_servicio ? "Editar servicio" : "Nuevo servicio"}
          </Typography>
          <Button startIcon={<CloseIcon />} onClick={closePanel} disabled={processing}>
            Cerrar
          </Button>
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={12} sm={5}>
            <TextField
              label="Código"
              value={formData.codigo}
              onChange={(e) => setFormData((p) => ({ ...p, codigo: e.target.value }))}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={7}>
            <TextField
              label="Categoría"
              value={formData.categoria}
              onChange={(e) => setFormData((p) => ({ ...p, categoria: e.target.value }))}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Nombre"
              value={formData.nombre}
              onChange={(e) => setFormData((p) => ({ ...p, nombre: e.target.value }))}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Precio base (cobro)"
              type="number"
              inputProps={{ step: "0.01", min: 0 }}
              value={formData.precio_base}
              onChange={(e) => setFormData((p) => ({ ...p, precio_base: e.target.value }))}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Costo externo (boleta/recarga)"
              type="number"
              inputProps={{ step: "0.01", min: 0 }}
              value={formData.costo_externo_base}
              onChange={(e) => setFormData((p) => ({ ...p, costo_externo_base: e.target.value }))}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Recargo % (solo categoría FREE)"
              type="number"
              inputProps={{ step: "0.01", min: 0 }}
              value={formData.porcentaje_recargo_costo_ext}
              onChange={(e) => setFormData((p) => ({ ...p, porcentaje_recargo_costo_ext: e.target.value }))}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              select
              label="Activo"
              value={formData.activo ? 1 : 0}
              onChange={(e) => setFormData((p) => ({ ...p, activo: Number(e.target.value) === 1 }))}
              fullWidth
            >
              <MenuItem value={1}>Activo</MenuItem>
              <MenuItem value={0}>Inactivo</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <Button variant="contained" fullWidth onClick={handleSave} disabled={processing}>
              Guardar
            </Button>
          </Grid>
        </Grid>
      </Drawer>
    </PageContainer>
  );
};

export default ServiciosCatalogo;
